import { useMemo } from 'react';

import { useGetLeadsFilterDataQuery } from '@app/api/Leads';
import { useListForFiltersQuery } from '@app/api/SalesFunnels/api';
import { useUserPrint } from '@app/hooks';

import { mapLeadsFilterDataToAssigneeOptions } from '../model/adapters/mapLeadsFilterDataToAssigneeOptions';
import { mapLeadsFilterDataToCheckboxOptions } from '../model/adapters/mapLeadsFilterDataToCheckboxOptions';
import { mapLeadsFilterDataToTagOptions } from '../model/adapters/mapLeadsFilterDataToTagOptions';
import { mapSalesFunnelsToTreeData } from '../model/adapters/mapSalesFunnelsToTreeData';

// Loads the registry filter dictionaries through the new `src/api` areas and
// maps them to filter options, owned by Leads (no dependency on the old
// `LeadsProvider`). The checkbox/tags dictionary comes from `GetLeadsFilterData`
// and the funnel tree from `SalesFunnels/ListForFilters`; RTK Query caches both.
export const useLeadsRegistryFilterData = () => {
  const { params, user } = useUserPrint();
  const activeBranchId = params.ActiveBranchId;
  const userId = params.UserId;
  const userName = `${user?.nameFirst ?? ''} ${user?.nameLast ?? ''}`.trim();

  const { data: filterData, isFetching: isLeadsFilterDataFetching } =
    useGetLeadsFilterDataQuery(
      { ActiveBranchId: activeBranchId },
      { skip: !activeBranchId }
    );

  const { data: funnels, isFetching: isFunnelsFetching } =
    useListForFiltersQuery(
      { ActiveBranchId: activeBranchId },
      { skip: !activeBranchId }
    );

  const checkboxOptions = useMemo(
    () => mapLeadsFilterDataToCheckboxOptions(filterData, { userId, userName }),
    [filterData, userId, userName]
  );

  const tagOptions = useMemo(
    () => mapLeadsFilterDataToTagOptions(filterData),
    [filterData]
  );

  const funnelTreeData = useMemo(
    () => mapSalesFunnelsToTreeData(funnels ?? []),
    [funnels]
  );

  const assigneeOptions = useMemo(
    () => mapLeadsFilterDataToAssigneeOptions(filterData),
    [filterData]
  );

  return {
    checkboxOptions,
    tagOptions,
    funnelTreeData,
    funnels: funnels ?? [],
    assigneeOptions,
    isFilterDataLoading: isLeadsFilterDataFetching || isFunnelsFetching,
  };
};
